import type { ReactNode } from 'react';
import { Card, CardBody, CardFooter, CardHeader } from './Card';
import type { CardVariant } from './Card';
import { CardActions } from './ActionHosts';
import type { ActionDescriptor } from './ActionHosts';
import { Stack } from './Layout';

// ------------------------------------------------------------------
// EntityCard — story 2-147ta (P10 §2.7 row 5 "Product/data cards", DOD).
// The generic "entity summary" card shape `RailPanel.tsx`'s own header
// comment already anticipated by name ("a stacked access card per share
// link ... typically `DataList`/`EntityCard` (UI-DS-01, later) ... until
// those land, plain rows") — a titled record with optional badges, a
// wrapping description, meta facts and a governed action footer, composed
// entirely from `Card`'s own slot family.
//
// NOT a replacement for `TemplateCard.tsx`. That card is module-local
// (`modules/templates`) and content-specific (thumbnail, status/origin
// badges, section-chip overflow, a details side sheet, four distinct
// permission-gated actions) — P10 §2.7 row 5 already investigated and
// settled its scoping ("module-local per the Collection form rule ... not
// re-litigated"), and migrating it onto `EntityCard` is explicit page-
// migration work this story is out of scope for (`2-147w*`). `EntityCard`
// is the general shape a FUTURE second/third entity-summary-card consumer
// reaches for instead of hand-rolling `TemplateCard`'s own border/padding/
// radius a second time — the same rule-of-three discipline `KpiCard`'s own
// citation already establishes elsewhere in this table.
//
// THE ONE-CONTROL PATTERN, applied narrowly. `onOpen` wraps only the TITLE
// in a real `<button>` (not the whole card, unlike `TaskCard`) — because an
// entity card's body/footer commonly carries its OWN independently
// clickable actions (badges are not interactive, but a footer `CardActions`
// button is), so wrapping the entire card in one control would nest
// interactive elements. Same accessible-name convention as `TaskCard.tsx`:
// defaults to "Open <title>" so the accessible name is never just the
// literal title text repeated as a control's own unlabelled action.
// ------------------------------------------------------------------

export interface EntityCardProps {
  /** The record's identity. Required — every entity card is about something named. */
  title: string;
  subtitle?: ReactNode;
  /** A wrapping row of status/origin badges (`StatusChip`/`Badge`) under the title. */
  badges?: ReactNode;
  /** Occasional-detail prose — a wrapping description. Composed inside `CardBody`. */
  description?: ReactNode;
  /** Short facts row (chips, small text) — `CardBody`'s second block, rendered under `description`. */
  meta?: ReactNode;
  /** Rendered via `2-147r`'s `CardActions` in the footer — never a raw `Button` list. */
  actions?: ActionDescriptor[];
  /** Makes the title the control that opens the record (see header comment for why only the title, not the whole card). Omitted, the title is plain text. */
  onOpen?: () => void;
  /** Announced name for the title control. Defaults to `Open <title>`. */
  openLabel?: string;
  variant?: CardVariant;
  className?: string;
  'data-testid'?: string;
}

export function EntityCard({
  title,
  subtitle,
  badges,
  description,
  meta,
  actions,
  onOpen,
  openLabel,
  variant = 'flat',
  className = '',
  'data-testid': testId,
}: EntityCardProps): JSX.Element {
  const titleNode: ReactNode = onOpen ? (
    <button
      type="button"
      onClick={onOpen}
      aria-label={openLabel ?? `Open ${title}`}
      className="focus-ring -m-1 rounded p-1 text-left"
    >
      {title}
    </button>
  ) : (
    title
  );
  const hasBody = description != null || meta != null;
  const hasFooter = actions != null && actions.length > 0;

  return (
    <Card variant={variant} className={className} {...(testId ? { 'data-testid': testId } : {})}>
      <CardHeader title={titleNode} subtitle={subtitle} badges={badges} />
      {hasBody && (
        <CardBody>
          <Stack gap={2}>
            {description != null && <p>{description}</p>}
            {meta != null && <div>{meta}</div>}
          </Stack>
        </CardBody>
      )}
      {hasFooter && (
        <CardFooter>
          <CardActions actions={actions ?? []} />
        </CardFooter>
      )}
    </Card>
  );
}
